import { TFunction } from 'i18next';

//
// The codes of the typed refusals the API returns in its ProblemDetails body (code + message). Mirrors the
// codes the SkillControl API raises; a code without a case below renders the generic unknown-error text.
//
export enum ControllerExceptionCodes {
  Code_General_NotAuthorized = 'IP0001',
  Code_General_NotFound = 'IP0002',
  // Identity providers. A refusal of the login itself arrives as a 401 whose body carries the code (see
  // getIdentityProviderErrorCode).
  Code_Identity_ProviderNotSupported = 'IDP0001',
  Code_Identity_BusinessAccountRequired = 'IDP0002',
  Code_Identity_MicrosoftIdentityRequired = 'IDP0003',
  Code_Identity_DirectoryMismatch = 'IDP0004',
  Code_Identity_EmailNotVerified = 'IDP0005',
  Code_Identity_DirectoryConnectionRequired = 'IDP0006',
  Code_Identity_DirectoryConnectionFailed = 'IDP0007',
  Code_Identity_AccountNotClaimable = 'IDP0008',
  Code_Identity_DirectoryConsentRequired = 'IDP0009',
  // Tenant Admin: an OpenID Connect identity issuer refused on save, or an added issuer that already exists; the
  // message says which
  Code_Identity_IssuerInvalid = 'IDP0010',
  // Google Workspace directory connection: no proof that the organization owns the domain, or another
  // organization holds the domain verified
  Code_Identity_WorkspaceDomainNotOwned = 'IDP0011',
  Code_Identity_WorkspaceDomainTaken = 'IDP0012',
  Code_Identity_DirectorySyncInProgress = 'IDP0013',
  // Microsoft Graph on behalf of the user: the organization has not consented to the delegated permissions
  Code_Auth_NewPermissionsRequired = 'AUTH0006',
  // POST tenants for a directory that already has an organization
  Code_Tenant_DirectoryAlreadyRegistered = 'TENANT0001',
  // the caller signed in but has not created an organization yet (replaced HELLO0001)
  Code_Tenant_NoOrganization = 'TENANT0005',
  // Executions: why a request was refused, or why an execution ended as Failed (its errorCode)
  Code_Execution_ModelNotConfigured = 'EXEC0001',
  Code_Execution_StorageNotConfigured = 'EXEC0002',
  Code_Execution_TimedOut = 'EXEC0003',
  Code_Execution_ModelFailed = 'EXEC0004',
  Code_Execution_RequestInvalid = 'EXEC0005',
  Code_Execution_AlreadyExists = 'EXEC0006',
  Code_Execution_Failed = 'EXEC0007',
  // a document of an execution that is still running cannot be saved to a provider yet
  Code_Execution_StillRunning = 'EXEC0008',
  // a restart of the API stopped the run before it ended
  Code_Execution_Interrupted = 'EXEC0009',
  // a paused run cannot continue: its saved state is missing or unreadable, or could not be saved at the pause
  Code_Execution_RunStateUnavailable = 'EXEC0010',
  // a stop request for an execution that has already ended (completed, failed or canceled)
  Code_Execution_AlreadyEnded = 'EXEC0011',
  // a run_skill call that is not allowed (phase 10): only a request without a Skill, one level deep; a ToolCall refusal code
  Code_Execution_RunSkillNotAllowed = 'EXEC0012',
  // a document cannot be mailed to the caller (phase 61): mail is not set up, no usable address, the file unreadable
  Code_Execution_MailUnavailable = 'EXEC0013',
  // Tenant Admin: the operator's own organization, an organization that is not suspended, an unknown subscription
  Code_Tenant_CannotDeleteOwnTenant = 'TENANT0002',
  Code_Tenant_NotSuspended = 'TENANT0003',
  Code_Tenant_InvalidSubscriptionType = 'TENANT0004',
  // Backups, with ISOPlanner's numbers (BACKUP0002, 0004, 0005 and 0006 are not used)
  Code_Backup_AlreadyInProgress = 'BACKUP0001',
  Code_Backup_NotReadyToRestore = 'BACKUP0003',
  Code_Backup_PointInTimeUnavailable = 'BACKUP0007',
  Code_Backup_HistoryUnavailable = 'BACKUP0008',
  Code_Backup_TenantHeldByFailedRestore = 'BACKUP0009',
  // other users were active in the last hour and the restore was not forced
  Code_Backup_OtherUsersActive = 'BACKUP0010',
  // Document providers, with ISOPlanner's numbers (DOCPROV0007, 0010 and 0014 are not used)
  Code_DocumentProvider_NotSupported = 'DOCPROV0001',
  Code_DocumentProvider_ConnectionNotFound = 'DOCPROV0002',
  Code_DocumentProvider_ConnectionDisabled = 'DOCPROV0003',
  Code_DocumentProvider_AuthFailed = 'DOCPROV0004',
  Code_DocumentProvider_ItemNotFound = 'DOCPROV0005',
  Code_DocumentProvider_RemoteError = 'DOCPROV0006',
  Code_DocumentProvider_HostNotAllowed = 'DOCPROV0008',
  Code_DocumentProvider_RateLimited = 'DOCPROV0009',
  Code_DocumentProvider_InvalidRequest = 'DOCPROV0011',
  // a user-delegated connection the user has not connected their own account to: the client offers to connect
  Code_DocumentProvider_UserGrantRequired = 'DOCPROV0012',
  Code_DocumentProvider_WorkspaceNotAuthorized = 'DOCPROV0013',
  // a Google Workspace connection refuses a user outside its domain instead of acting as the service account
  Code_DocumentProvider_UserNotInWorkspace = 'DOCPROV0015',
  // a library read on a connection that uses the organization's credential: reads use each user's own account only
  Code_DocumentProvider_UserAccountsRequired = 'DOCPROV0016',
  // Skills: a refused write or lifecycle action. SKILL0001, SKILL0004, SKILL0006 and SKILL0007 carry the detail in
  // the message
  Code_Skill_RequestInvalid = 'SKILL0001',
  Code_Skill_KeyExists = 'SKILL0002',
  Code_Skill_VersionNotEditable = 'SKILL0003',
  Code_Skill_InvalidTransition = 'SKILL0004',
  Code_Skill_AuthorCannotApprove = 'SKILL0005',
  Code_Skill_YamlInvalid = 'SKILL0006',
  Code_Skill_VersionNumberInvalid = 'SKILL0007',
  Code_Skill_NotAllowed = 'SKILL0008',
  Code_Skill_PublishedCannotBeDeleted = 'SKILL0009',
  Code_Skill_OpenVersionExists = 'SKILL0010',
  Code_Skill_NotActive = 'SKILL0011',
  Code_Skill_OwnerInvalid = 'SKILL0012',
  // the drafting or editing assistant gave no usable Skill after one repair; nothing was stored
  Code_Skill_AuthoringFailed = 'SKILL0013',
  // POST executions named a Skill version the caller cannot run (not the published version of an Active Skill in
  // the caller's audience); the conversation routes the request again
  Code_Skill_VersionNotRunnable = 'SKILL0014',
  // no approved route: the request names no Skill and the organization does not allow requests without one
  Code_Skill_AdHocNotAllowed = 'SKILL0015',
  // approve or publish of a version whose tools or operations the organization's capabilities do not provide (phase 11);
  // the message lists the items
  Code_Skill_RequirementsNotSatisfied = 'SKILL0016',
  // phase 21: a Skill authoring conversation holds as many messages as it can; the owner starts a new one, which
  // continues from the saved draft
  Code_Skill_SessionFull = 'SKILL0017',
  // phase 21: another message of the Skill authoring conversation is being answered; this one was not stored
  Code_Skill_SessionTurnConflict = 'SKILL0018',
  Code_Skill_CheckKeyChanged = 'SKILL0019',
  Code_Skill_CheckKindChangeUnconfirmed = 'SKILL0020',
  // phase 62: the authoring conversation saved no change, so it submits nothing
  Code_Skill_SessionUnchanged = 'SKILL0021',
  // Capabilities, the admin lifecycle. CAP0001, CAP0004, CAP0006, CAP0007, CAP0012 and CAP0013 carry the detail in
  // the message
  Code_Capability_RequestInvalid = 'CAP0001',
  Code_Capability_KeyExists = 'CAP0002',
  Code_Capability_VersionNotEditable = 'CAP0003',
  Code_Capability_InvalidTransition = 'CAP0004',
  Code_Capability_EndpointNotAllowed = 'CAP0006',
  Code_Capability_LimitReached = 'CAP0007',
  Code_Capability_ToolOverrideRequired = 'CAP0008',
  Code_Capability_OpenVersionExists = 'CAP0009',
  Code_Capability_NotDisabled = 'CAP0010',
  Code_Capability_ToolNotInCatalog = 'CAP0011',
  Code_Capability_ConnectorUnavailable = 'CAP0012',
  // the server offered no usable client registration for an OAuth capability saved without a client id
  Code_Capability_ClientRegistrationFailed = 'CAP0013',
  // Capabilities, the user's own connection. CAP0052 carries the detail, CAP0055 the provider's OAuth error code
  Code_Capability_NotAvailable = 'CAP0050',
  Code_Capability_AuthModeMismatch = 'CAP0051',
  Code_Capability_AuthorizationNotConfigured = 'CAP0052',
  Code_Capability_AuthorizationMismatch = 'CAP0053',
  Code_Capability_AuthorizationExpired = 'CAP0054',
  Code_Capability_AuthorizationFailed = 'CAP0055',
  Code_Capability_TokenInvalid = 'CAP0056',
  // phase 51: a credential an organization admin enters on someone else's behalf is a service principal's only
  Code_Capability_ServicePrincipalOnly = 'CAP0057',
  // Capabilities at run time: the tool gate and the preflight. CAP0109-CAP0111 refuse POST executions and end a run
  // whose credential is lost mid-run; the others are the refusal code of a ToolCall step
  // CAP0100: retired 2026-10-12 (phase 10: a request without a Skill calls the approved Capabilities); never sent again,
  // kept for the tool calls recorded before
  Code_Capability_NotForAdHoc = 'CAP0100',
  Code_Capability_NotRequiredBySkill = 'CAP0101',
  Code_Capability_Unavailable = 'CAP0102',
  Code_Capability_ChangedDuringRun = 'CAP0103',
  Code_Capability_OperationNotAllowed = 'CAP0104',
  Code_Capability_PermissionDenied = 'CAP0105',
  Code_Capability_ApprovalRequired = 'CAP0106',
  Code_Capability_ToolCallLimitReached = 'CAP0107',
  Code_Capability_CapabilityCallLimitReached = 'CAP0108',
  Code_Capability_AuthorizationRequired = 'CAP0109',
  Code_Capability_NeedsReauthorization = 'CAP0110',
  Code_Capability_NotConfigured = 'CAP0111',
  Code_Capability_ProviderUnavailable = 'CAP0112',
  Code_Capability_SkillFileNotFound = 'CAP0113',
  Code_Capability_PolicyDenied = 'CAP0114',
  // not run: an earlier call of the same model turn paused the run for an approval
  Code_Capability_NotRunAtPause = 'CAP0115',
  // the Skill version requires tools or operations the organization's capabilities do not provide (phase 11): the run
  // is refused after CAP0109-CAP0111; the message lists the items
  Code_Capability_RequirementsNotSatisfied = 'CAP0116',
  // Policies, the admin rules. POL0001, POL0003 and POL0005 carry the detail in the message
  Code_Policy_RequestInvalid = 'POL0001',
  Code_Policy_ApproverGroupInvalid = 'POL0002',
  Code_Policy_SkillInvalid = 'POL0003',
  Code_Policy_InvalidTransition = 'POL0004',
  Code_Policy_AlreadyExists = 'POL0005',
  // Policies at run time: the refusal code of a ToolCall step (POL0100, POL0101) or of a Failed execution (POL0102)
  Code_Policy_WriteNotAllowed = 'POL0100',
  Code_Policy_RuleUnreadable = 'POL0101',
  Code_Policy_TokenLimitReached = 'POL0102',
  // Approvals, the decisions on paused tool calls. APPR0001 carries the detail in the message
  Code_Approval_RequestInvalid = 'APPR0001',
  Code_Approval_NotAllowed = 'APPR0002',
  Code_Approval_AlreadyDecided = 'APPR0003',
  // Approvals at run time: the errorCode of a Failed execution
  Code_Approval_NoApprover = 'APPR0100',
  Code_Approval_Rejected = 'APPR0101',
  Code_Approval_Expired = 'APPR0102',
  // The runtime audit record. AUDIT0001 carries the detail in the message; AUDIT0002 refuses an export over 50,000 events
  Code_Audit_RequestInvalid = 'AUDIT0001',
  Code_Audit_ExportTooLarge = 'AUDIT0002',
  // The organization's own AI models. AIMODEL0001 carries the detail in the message; AIMODEL0002 refuses the switch
  // before all three profiles are saved and tested
  Code_AIModel_Invalid = 'AIMODEL0001',
  Code_AIModel_NotReady = 'AIMODEL0002',
  // The Skill analytics: a period whose start is not before its end, or longer than 366 days (detail in the message)
  Code_Analytics_RequestInvalid = 'ANALYTICS0001',
  // Administration > Users: the system roles and the login of the organization's users, and a user added by id (phase
  // 51: USER0007 the id or the e-mail address is taken). USER0001 carries the detail in the message
  Code_User_RequestInvalid = 'USER0001',
  Code_User_RoleNotAssignable = 'USER0002',
  Code_User_SelfEditNotAllowed = 'USER0003',
  Code_User_OrgAdminRequired = 'USER0004',
  Code_User_LastOrgAdmin = 'USER0005',
  Code_User_NoFreeLicense = 'USER0006',
  Code_User_Exists = 'USER0007',
  // Skill tests: the tests/*.yaml files of a version and their runs. TEST0001 and TEST0006 carry the detail in the
  // message
  Code_Test_YamlInvalid = 'TEST0001',
  Code_Test_TooManyCases = 'TEST0002',
  Code_Test_RunInProgress = 'TEST0003',
  Code_Test_NoCases = 'TEST0004',
  Code_Test_VersionNotTestable = 'TEST0005',
  Code_Test_RequestInvalid = 'TEST0006',
  Code_Test_NoBaseline = 'TEST0007',
  // Save to... of a document of a simulated execution: it is built from mocked data and never saved to a provider
  Code_Test_SimulatedDocumentNotSaved = 'TEST0008',
  // Skill tests at run time: the refusal code of a simulated tool call (TEST0100, TEST0101) or why a case was not
  // judged (TEST0102, TEST0103)
  Code_Test_CallNotMocked = 'TEST0100',
  Code_Test_CaseNotAvailable = 'TEST0101',
  Code_Test_CaseTimedOut = 'TEST0102',
  Code_Test_RunInterrupted = 'TEST0103',
  // The schedules of a Skill at save time (SCHED0001-SCHED0099). SCHED0001, SCHED0003 and SCHED0005 carry the detail in
  // the message
  Code_Schedule_RequestInvalid = 'SCHED0001',
  Code_Schedule_NotAllowed = 'SCHED0002',
  Code_Schedule_PatternInvalid = 'SCHED0003',
  Code_Schedule_TimeZoneInvalid = 'SCHED0004',
  Code_Schedule_SkillNotRunnable = 'SCHED0005',
  Code_Schedule_SharePointNotAllowed = 'SCHED0006',
  Code_Schedule_ConnectionNotFound = 'SCHED0007',
  Code_Schedule_LimitReached = 'SCHED0008',
  // a save-time warning only (never thrown): mail delivery while mail is not set up on the server
  Code_Schedule_MailNotSetUp = 'SCHED0009',
  // phase 61: a recipient of the mail who cannot receive it; SharePoint without the owner's delivery permission
  Code_Schedule_RecipientInvalid = 'SCHED0010',
  Code_Schedule_DeliveryGrantRequired = 'SCHED0011',
  // Schedules at run time: why an occurrence or its delivery failed (SCHED0100 and up). Never a refusal of a request:
  // the code is the prefix of a schedule's lastError ("SCHED0101: reason") and the code of its Schedule audit event
  Code_Schedule_RecurrenceUnusable = 'SCHED0100',
  Code_Schedule_RunFailed = 'SCHED0101',
  Code_Schedule_DeliveryFailed = 'SCHED0102',
  Code_Schedule_QueueFailed = 'SCHED0103',
  // Run-to-skill proposals (phase 10): a note or comment too long or missing, not a Skill owner, a run that has not
  // completed, a proposal decided already
  Code_Proposal_RequestInvalid = 'PROP0001',
  Code_Proposal_NotAllowed = 'PROP0002',
  Code_Proposal_ExecutionNotProposable = 'PROP0003',
  Code_Proposal_AlreadyDecided = 'PROP0004',
  // fix-skill-from-run (phase 11): not a finished run of a Skill, or a simulation
  Code_Proposal_ExecutionNotFixable = 'PROP0005',
  // phase 62: the requester has an open improvement request on this Skill already
  Code_Proposal_RequestOpen = 'PROP0006',
  // The content marketplace (phase 14): not a publisher, a request to correct, a key taken, content without a
  // published or approved version, a version another operator decided first
  Code_Pack_NotAPublisher = 'PACK0001',
  Code_Pack_RequestInvalid = 'PACK0002',
  Code_Pack_KeyExists = 'PACK0003',
  Code_Pack_ContentNotPublishable = 'PACK0004',
  Code_Pack_VersionNotInState = 'PACK0005',
  // The capability catalog and suggestions (phase 16): a request to correct, a suggestion another administrator
  // decided first, a catalog entry to correct, a catalog key taken
  Code_Suggestion_RequestInvalid = 'SUGG0001',
  Code_Suggestion_NotInState = 'SUGG0002',
  Code_Suggestion_CatalogEntryInvalid = 'SUGG0003',
  Code_Suggestion_CatalogKeyExists = 'SUGG0004',
  // phase 17: the assistant drafted no valid Capability for a suggestion; the suggestion stays open
  Code_Suggestion_DraftFailed = 'SUGG0005',
  // phase 23: the refusal code of a data_operations or chart_render call (on the tool call, never an HTTP answer)
  Code_Data_SpecInvalid = 'DATA0100',
  Code_Data_OutputNotFound = 'DATA0101',
  Code_Data_OutputUnavailableAfterPause = 'DATA0102',
  Code_Data_InputTooLarge = 'DATA0103',
  Code_Data_ResultTooLarge = 'DATA0104',
  Code_Data_CallLimitReached = 'DATA0105',
  Code_Data_TypeMismatch = 'DATA0106',
  Code_Data_NotJson = 'DATA0107',
  Code_Data_DivisionByZero = 'DATA0108',
  Code_Data_ChartInvalid = 'DATA0110',
  Code_Data_ChartTooLarge = 'DATA0111',
  // phase 18: the organization's AI credits are used up (HTTP 402, also an execution's errorCode)
  Code_Credit_NoCreditsAvailable = 'CREDIT0001',
  // the mail to the administrators about used-up credits cannot go out: mail is not set up on the server
  Code_Credit_MailNotSetUp = 'CREDIT0002',
  // phase 25: the AI Credit Package cannot be bought here, or the request does not fit the package (detail in the message)
  Code_Credit_PackageNotAvailable = 'CREDIT0003',
  Code_Credit_PackageRequestInvalid = 'CREDIT0004',
  // phase 25: the plans. No plan can be bought here; a plan request to correct; no plan or customer yet (detail in the
  // message)
  Code_Billing_PlanNotAvailable = 'BILLING0001',
  Code_Billing_PlanRequestInvalid = 'BILLING0002',
  Code_Billing_NoPlan = 'BILLING0003',
  // phase 28 P-C: a Skill's documents. Not in the Library, no read grant, not there for the user (each with the API's
  // sentence that names the path), a limit of the document tools, a file that could not be turned into text, a type
  // without a reader
  Code_Library_NotInLibrary = 'LIB0001',
  Code_Library_NoReadGrant = 'LIB0002',
  Code_Library_NotFound = 'LIB0003',
  Code_Library_LimitReached = 'LIB0004',
  Code_Library_ExtractionFailed = 'LIB0005',
  Code_Library_TypeNotReadable = 'LIB0006',
  // phase 28: the Library. The name is taken; a field to correct (detail in the message); the connection reads with
  // the organization's own credential
  Code_Library_NameTaken = 'LIB0010',
  Code_Library_RequestInvalid = 'LIB0011',
  Code_Library_ConnectionNotSupported = 'LIB0012',
  // phase 44: the connected apps (the GitHub App first). Not configured on this server; a claim to correct (detail in
  // the message); unknown at the provider; linked to another organization; not installed or suspended for a connect;
  // the provider unreachable
  Code_App_NotConfigured = 'APP0001',
  Code_App_RequestInvalid = 'APP0002',
  Code_App_InstallationNotFound = 'APP0003',
  Code_App_InstallationClaimed = 'APP0004',
  Code_App_InstallationUnavailable = 'APP0005',
  Code_App_ProviderUnavailable = 'APP0006',
}

// The refusals of POST executions for a Skill version whose required capabilities are not usable by the caller, or whose
// requirements the capabilities do not provide, or a documents entry a run of the caller could not read (phase 28) (the
// preflight): the confirmation card refreshes its preflight on them
export const capabilityPreflightRefusalCodes: string[] = [
  ControllerExceptionCodes.Code_Capability_AuthorizationRequired,
  ControllerExceptionCodes.Code_Capability_NeedsReauthorization,
  ControllerExceptionCodes.Code_Capability_NotConfigured,
  ControllerExceptionCodes.Code_Capability_RequirementsNotSatisfied,
  ControllerExceptionCodes.Code_Library_NotInLibrary,
  ControllerExceptionCodes.Code_Library_NoReadGrant,
  ControllerExceptionCodes.Code_Library_NotFound,
  ControllerExceptionCodes.Code_Library_ConnectionNotSupported,
];

export default class ControllerException {
  code: string;

  message?: string | undefined;

  constructor() {
    this.code = '';
  }

  // `message` carries the exception's Message field from the API, for codes whose text needs dynamic data.
  static getMessage(code: ControllerExceptionCodes, t: TFunction<string[]>, message?: string): string {
    switch (code) {
      case ControllerExceptionCodes.Code_General_NotAuthorized:
        return t('exceptions:General.NoAuth');
      case ControllerExceptionCodes.Code_General_NotFound:
        return t('exceptions:General.NotFound');
      case ControllerExceptionCodes.Code_Identity_ProviderNotSupported:
        return t('exceptions:Identity.ProviderNotSupported');
      case ControllerExceptionCodes.Code_Identity_BusinessAccountRequired:
        return t('exceptions:Identity.BusinessAccountRequired');
      case ControllerExceptionCodes.Code_Identity_MicrosoftIdentityRequired:
        return t('exceptions:Identity.MicrosoftIdentityRequired');
      case ControllerExceptionCodes.Code_Identity_DirectoryMismatch:
        return t('exceptions:Identity.DirectoryMismatch');
      case ControllerExceptionCodes.Code_Identity_EmailNotVerified:
        return t('exceptions:Identity.EmailNotVerified');
      case ControllerExceptionCodes.Code_Identity_DirectoryConnectionRequired:
        return t('exceptions:Identity.DirectoryConnectionRequired');
      case ControllerExceptionCodes.Code_Identity_DirectoryConnectionFailed:
        return t('exceptions:Identity.DirectoryConnectionFailed', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Identity_AccountNotClaimable:
        return t('exceptions:Identity.AccountNotClaimable', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Identity_DirectoryConsentRequired:
        return t('exceptions:Identity.DirectoryConsentRequired');
      case ControllerExceptionCodes.Code_Identity_IssuerInvalid:
        return t('exceptions:Identity.IssuerInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Identity_WorkspaceDomainNotOwned:
        return t('exceptions:Identity.WorkspaceDomainNotOwned');
      case ControllerExceptionCodes.Code_Identity_WorkspaceDomainTaken:
        return t('exceptions:Identity.WorkspaceDomainTaken');
      case ControllerExceptionCodes.Code_Identity_DirectorySyncInProgress:
        return t('exceptions:Identity.DirectorySyncInProgress');
      case ControllerExceptionCodes.Code_Auth_NewPermissionsRequired:
        return t('exceptions:Auth.NewPermissionsRequired');
      case ControllerExceptionCodes.Code_Tenant_DirectoryAlreadyRegistered:
        return t('exceptions:Tenant.DirectoryAlreadyRegistered');
      case ControllerExceptionCodes.Code_Tenant_NoOrganization:
        return t('exceptions:Tenant.NoOrganization');
      case ControllerExceptionCodes.Code_Execution_ModelNotConfigured:
        return t('exceptions:Execution.ModelNotConfigured');
      case ControllerExceptionCodes.Code_Execution_StorageNotConfigured:
        return t('exceptions:Execution.StorageNotConfigured');
      case ControllerExceptionCodes.Code_Execution_TimedOut:
        return t('exceptions:Execution.TimedOut');
      case ControllerExceptionCodes.Code_Execution_ModelFailed:
        return t('exceptions:Execution.ModelFailed');
      case ControllerExceptionCodes.Code_Execution_RequestInvalid:
        return t('exceptions:Execution.RequestInvalid');
      case ControllerExceptionCodes.Code_Execution_AlreadyExists:
        return t('exceptions:Execution.AlreadyExists');
      case ControllerExceptionCodes.Code_Execution_Failed:
        return t('exceptions:Execution.Failed');
      case ControllerExceptionCodes.Code_Execution_StillRunning:
        return t('exceptions:Execution.StillRunning');
      case ControllerExceptionCodes.Code_Execution_Interrupted:
        return t('exceptions:Execution.Interrupted');
      case ControllerExceptionCodes.Code_Execution_RunStateUnavailable:
        return t('exceptions:Execution.RunStateUnavailable');
      case ControllerExceptionCodes.Code_Execution_AlreadyEnded:
        return t('exceptions:Execution.AlreadyEnded');
      case ControllerExceptionCodes.Code_Execution_RunSkillNotAllowed:
        return t('exceptions:Execution.RunSkillNotAllowed');
      case ControllerExceptionCodes.Code_Execution_MailUnavailable:
        return t('exceptions:Execution.MailUnavailable');
      case ControllerExceptionCodes.Code_Tenant_CannotDeleteOwnTenant:
        return t('exceptions:Tenant.CannotDeleteOwnTenant');
      case ControllerExceptionCodes.Code_Tenant_NotSuspended:
        return t('exceptions:Tenant.NotSuspended');
      case ControllerExceptionCodes.Code_Tenant_InvalidSubscriptionType:
        return t('exceptions:Tenant.InvalidSubscriptionType');
      case ControllerExceptionCodes.Code_Backup_AlreadyInProgress:
        return t('exceptions:Backup.AlreadyInProgress');
      case ControllerExceptionCodes.Code_Backup_NotReadyToRestore:
        return t('exceptions:Backup.NotReadyToRestore');
      case ControllerExceptionCodes.Code_Backup_PointInTimeUnavailable:
        return t('exceptions:Backup.PointInTimeUnavailable');
      case ControllerExceptionCodes.Code_Backup_HistoryUnavailable:
        return t('exceptions:Backup.HistoryUnavailable');
      case ControllerExceptionCodes.Code_Backup_TenantHeldByFailedRestore:
        return t('exceptions:Backup.TenantHeldByFailedRestore');
      case ControllerExceptionCodes.Code_Backup_OtherUsersActive:
        return t('exceptions:Backup.OtherUsersActive');
      case ControllerExceptionCodes.Code_DocumentProvider_NotSupported:
        return t('exceptions:DocumentProvider.NotSupported');
      case ControllerExceptionCodes.Code_DocumentProvider_ConnectionNotFound:
        return t('exceptions:DocumentProvider.ConnectionNotFound');
      case ControllerExceptionCodes.Code_DocumentProvider_ConnectionDisabled:
        return t('exceptions:DocumentProvider.ConnectionDisabled');
      case ControllerExceptionCodes.Code_DocumentProvider_AuthFailed:
        return t('exceptions:DocumentProvider.AuthFailed');
      case ControllerExceptionCodes.Code_DocumentProvider_ItemNotFound:
        return t('exceptions:DocumentProvider.ItemNotFound');
      case ControllerExceptionCodes.Code_DocumentProvider_RemoteError:
        return t('exceptions:DocumentProvider.RemoteError');
      case ControllerExceptionCodes.Code_DocumentProvider_HostNotAllowed:
        return t('exceptions:DocumentProvider.HostNotAllowed');
      case ControllerExceptionCodes.Code_DocumentProvider_RateLimited:
        return t('exceptions:DocumentProvider.RateLimited');
      case ControllerExceptionCodes.Code_DocumentProvider_InvalidRequest:
        return t('exceptions:DocumentProvider.InvalidRequest', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_DocumentProvider_UserGrantRequired:
        return t('exceptions:DocumentProvider.UserGrantRequired');
      case ControllerExceptionCodes.Code_DocumentProvider_WorkspaceNotAuthorized:
        return t('exceptions:DocumentProvider.WorkspaceNotAuthorized');
      case ControllerExceptionCodes.Code_DocumentProvider_UserNotInWorkspace:
        return t('exceptions:DocumentProvider.UserNotInWorkspace');
      case ControllerExceptionCodes.Code_DocumentProvider_UserAccountsRequired:
        return t('exceptions:DocumentProvider.UserAccountsRequired');
      case ControllerExceptionCodes.Code_Skill_RequestInvalid:
        return t('exceptions:Skill.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Skill_KeyExists:
        return t('exceptions:Skill.KeyExists');
      case ControllerExceptionCodes.Code_Skill_VersionNotEditable:
        return t('exceptions:Skill.VersionNotEditable');
      case ControllerExceptionCodes.Code_Skill_InvalidTransition:
        return t('exceptions:Skill.InvalidTransition', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Skill_AuthorCannotApprove:
        return t('exceptions:Skill.AuthorCannotApprove');
      case ControllerExceptionCodes.Code_Skill_YamlInvalid:
        return t('exceptions:Skill.YamlInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Skill_VersionNumberInvalid:
        return t('exceptions:Skill.VersionNumberInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Skill_NotAllowed:
        return t('exceptions:Skill.NotAllowed');
      case ControllerExceptionCodes.Code_Skill_PublishedCannotBeDeleted:
        return t('exceptions:Skill.PublishedCannotBeDeleted');
      case ControllerExceptionCodes.Code_Skill_OpenVersionExists:
        return t('exceptions:Skill.OpenVersionExists');
      case ControllerExceptionCodes.Code_Skill_NotActive:
        return t('exceptions:Skill.NotActive');
      case ControllerExceptionCodes.Code_Skill_OwnerInvalid:
        return t('exceptions:Skill.OwnerInvalid');
      case ControllerExceptionCodes.Code_Skill_AuthoringFailed:
        return t('exceptions:Skill.AuthoringFailed', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Skill_VersionNotRunnable:
        return t('exceptions:Skill.VersionNotRunnable');
      case ControllerExceptionCodes.Code_Skill_AdHocNotAllowed:
        return t('exceptions:Skill.AdHocNotAllowed');
      case ControllerExceptionCodes.Code_Skill_RequirementsNotSatisfied:
        return t('exceptions:Skill.RequirementsNotSatisfied', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Skill_SessionFull:
        return t('exceptions:Skill.SessionFull');
      case ControllerExceptionCodes.Code_Skill_SessionTurnConflict:
        return t('exceptions:Skill.SessionTurnConflict');
      case ControllerExceptionCodes.Code_Skill_CheckKeyChanged:
        return t('exceptions:Skill.CheckKeyChanged');
      case ControllerExceptionCodes.Code_Skill_CheckKindChangeUnconfirmed:
        return t('exceptions:Skill.CheckKindChangeUnconfirmed');
      case ControllerExceptionCodes.Code_Skill_SessionUnchanged:
        return t('exceptions:Skill.SessionUnchanged');
      case ControllerExceptionCodes.Code_Capability_RequestInvalid:
        return t('exceptions:Capability.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_KeyExists:
        return t('exceptions:Capability.KeyExists');
      case ControllerExceptionCodes.Code_Capability_VersionNotEditable:
        return t('exceptions:Capability.VersionNotEditable');
      case ControllerExceptionCodes.Code_Capability_InvalidTransition:
        return t('exceptions:Capability.InvalidTransition', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_EndpointNotAllowed:
        return t('exceptions:Capability.EndpointNotAllowed', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_LimitReached:
        return t('exceptions:Capability.LimitReached', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_ToolOverrideRequired:
        return t('exceptions:Capability.ToolOverrideRequired');
      case ControllerExceptionCodes.Code_Capability_OpenVersionExists:
        return t('exceptions:Capability.OpenVersionExists');
      case ControllerExceptionCodes.Code_Capability_NotDisabled:
        return t('exceptions:Capability.NotDisabled');
      case ControllerExceptionCodes.Code_Capability_ToolNotInCatalog:
        return t('exceptions:Capability.ToolNotInCatalog');
      case ControllerExceptionCodes.Code_Capability_ConnectorUnavailable:
        return t('exceptions:Capability.ConnectorUnavailable', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_ClientRegistrationFailed:
        return t('exceptions:Capability.ClientRegistrationFailed', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_NotAvailable:
        return t('exceptions:Capability.NotAvailable');
      case ControllerExceptionCodes.Code_Capability_AuthModeMismatch:
        return t('exceptions:Capability.AuthModeMismatch');
      case ControllerExceptionCodes.Code_Capability_AuthorizationNotConfigured:
        return t('exceptions:Capability.AuthorizationNotConfigured', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_AuthorizationMismatch:
        return t('exceptions:Capability.AuthorizationMismatch');
      case ControllerExceptionCodes.Code_Capability_AuthorizationExpired:
        return t('exceptions:Capability.AuthorizationExpired');
      case ControllerExceptionCodes.Code_Capability_AuthorizationFailed:
        return t('exceptions:Capability.AuthorizationFailed', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Capability_TokenInvalid:
        return t('exceptions:Capability.TokenInvalid');
      case ControllerExceptionCodes.Code_Capability_ServicePrincipalOnly:
        return t('exceptions:Capability.ServicePrincipalOnly');
      case ControllerExceptionCodes.Code_Capability_NotForAdHoc:
        return t('exceptions:Capability.NotForAdHoc');
      case ControllerExceptionCodes.Code_Capability_NotRequiredBySkill:
        return t('exceptions:Capability.NotRequiredBySkill');
      case ControllerExceptionCodes.Code_Capability_Unavailable:
        return t('exceptions:Capability.Unavailable');
      case ControllerExceptionCodes.Code_Capability_ChangedDuringRun:
        return t('exceptions:Capability.ChangedDuringRun');
      case ControllerExceptionCodes.Code_Capability_OperationNotAllowed:
        return t('exceptions:Capability.OperationNotAllowed');
      case ControllerExceptionCodes.Code_Capability_PermissionDenied:
        return t('exceptions:Capability.PermissionDenied');
      case ControllerExceptionCodes.Code_Capability_ApprovalRequired:
        return t('exceptions:Capability.ApprovalRequired');
      case ControllerExceptionCodes.Code_Capability_ToolCallLimitReached:
        return t('exceptions:Capability.ToolCallLimitReached');
      case ControllerExceptionCodes.Code_Capability_CapabilityCallLimitReached:
        return t('exceptions:Capability.CapabilityCallLimitReached');
      case ControllerExceptionCodes.Code_Capability_AuthorizationRequired:
        return t('exceptions:Capability.AuthorizationRequired');
      case ControllerExceptionCodes.Code_Capability_NeedsReauthorization:
        return t('exceptions:Capability.NeedsReauthorization');
      case ControllerExceptionCodes.Code_Capability_NotConfigured:
        return t('exceptions:Capability.NotConfigured');
      case ControllerExceptionCodes.Code_Capability_ProviderUnavailable:
        return t('exceptions:Capability.ProviderUnavailable');
      case ControllerExceptionCodes.Code_Capability_SkillFileNotFound:
        return t('exceptions:Capability.SkillFileNotFound');
      case ControllerExceptionCodes.Code_Capability_PolicyDenied:
        return t('exceptions:Capability.PolicyDenied');
      case ControllerExceptionCodes.Code_Capability_NotRunAtPause:
        return t('exceptions:Capability.NotRunAtPause');
      // the API's item list stays out (English); the refreshed preflight card lists the items in the reader's language
      case ControllerExceptionCodes.Code_Capability_RequirementsNotSatisfied:
        return t('exceptions:Capability.RequirementsNotSatisfied');
      case ControllerExceptionCodes.Code_Policy_RequestInvalid:
        return t('exceptions:Policy.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Policy_ApproverGroupInvalid:
        return t('exceptions:Policy.ApproverGroupInvalid');
      case ControllerExceptionCodes.Code_Policy_SkillInvalid:
        return t('exceptions:Policy.SkillInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Policy_InvalidTransition:
        return t('exceptions:Policy.InvalidTransition');
      case ControllerExceptionCodes.Code_Policy_AlreadyExists:
        return t('exceptions:Policy.AlreadyExists', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Policy_WriteNotAllowed:
        return t('exceptions:Policy.WriteNotAllowed');
      case ControllerExceptionCodes.Code_Policy_RuleUnreadable:
        return t('exceptions:Policy.RuleUnreadable');
      case ControllerExceptionCodes.Code_Policy_TokenLimitReached:
        return t('exceptions:Policy.TokenLimitReached');
      case ControllerExceptionCodes.Code_Approval_RequestInvalid:
        return t('exceptions:Approval.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Approval_NotAllowed:
        return t('exceptions:Approval.NotAllowed');
      case ControllerExceptionCodes.Code_Approval_AlreadyDecided:
        return t('exceptions:Approval.AlreadyDecided');
      case ControllerExceptionCodes.Code_Approval_NoApprover:
        return t('exceptions:Approval.NoApprover');
      case ControllerExceptionCodes.Code_Approval_Rejected:
        return t('exceptions:Approval.Rejected');
      case ControllerExceptionCodes.Code_Approval_Expired:
        return t('exceptions:Approval.Expired');
      case ControllerExceptionCodes.Code_Audit_RequestInvalid:
        return t('exceptions:Audit.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Audit_ExportTooLarge:
        return t('exceptions:Audit.ExportTooLarge');
      case ControllerExceptionCodes.Code_AIModel_Invalid:
        return t('exceptions:AIModel.Invalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_AIModel_NotReady:
        return t('exceptions:AIModel.NotReady');
      case ControllerExceptionCodes.Code_Analytics_RequestInvalid:
        return t('exceptions:Analytics.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_User_RequestInvalid:
        return t('exceptions:User.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_User_RoleNotAssignable:
        return t('exceptions:User.RoleNotAssignable');
      case ControllerExceptionCodes.Code_User_SelfEditNotAllowed:
        return t('exceptions:User.SelfEditNotAllowed');
      case ControllerExceptionCodes.Code_User_OrgAdminRequired:
        return t('exceptions:User.OrgAdminRequired');
      case ControllerExceptionCodes.Code_User_LastOrgAdmin:
        return t('exceptions:User.LastOrgAdmin');
      case ControllerExceptionCodes.Code_User_NoFreeLicense:
        return t('exceptions:User.NoFreeLicense');
      case ControllerExceptionCodes.Code_User_Exists:
        return t('exceptions:User.Exists');
      case ControllerExceptionCodes.Code_Test_YamlInvalid:
        return t('exceptions:Test.YamlInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Test_TooManyCases:
        return t('exceptions:Test.TooManyCases');
      case ControllerExceptionCodes.Code_Test_RunInProgress:
        return t('exceptions:Test.RunInProgress');
      case ControllerExceptionCodes.Code_Test_NoCases:
        return t('exceptions:Test.NoCases');
      case ControllerExceptionCodes.Code_Test_VersionNotTestable:
        return t('exceptions:Test.VersionNotTestable');
      case ControllerExceptionCodes.Code_Test_RequestInvalid:
        return t('exceptions:Test.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Test_NoBaseline:
        return t('exceptions:Test.NoBaseline');
      case ControllerExceptionCodes.Code_Test_SimulatedDocumentNotSaved:
        return t('exceptions:Test.SimulatedDocumentNotSaved');
      case ControllerExceptionCodes.Code_Test_CallNotMocked:
        return t('exceptions:Test.CallNotMocked');
      case ControllerExceptionCodes.Code_Test_CaseNotAvailable:
        return t('exceptions:Test.CaseNotAvailable');
      case ControllerExceptionCodes.Code_Test_CaseTimedOut:
        return t('exceptions:Test.CaseTimedOut');
      case ControllerExceptionCodes.Code_Test_RunInterrupted:
        return t('exceptions:Test.RunInterrupted');
      case ControllerExceptionCodes.Code_Schedule_RequestInvalid:
        return t('exceptions:Schedule.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Schedule_NotAllowed:
        return t('exceptions:Schedule.NotAllowed');
      case ControllerExceptionCodes.Code_Schedule_PatternInvalid:
        return t('exceptions:Schedule.PatternInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Schedule_TimeZoneInvalid:
        return t('exceptions:Schedule.TimeZoneInvalid');
      case ControllerExceptionCodes.Code_Schedule_SkillNotRunnable:
        return t('exceptions:Schedule.SkillNotRunnable', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Schedule_SharePointNotAllowed:
        return t('exceptions:Schedule.SharePointNotAllowed');
      case ControllerExceptionCodes.Code_Schedule_ConnectionNotFound:
        return t('exceptions:Schedule.ConnectionNotFound');
      case ControllerExceptionCodes.Code_Schedule_LimitReached:
        return t('exceptions:Schedule.LimitReached');
      case ControllerExceptionCodes.Code_Schedule_MailNotSetUp:
        return t('exceptions:Schedule.MailNotSetUp');
      case ControllerExceptionCodes.Code_Schedule_RecipientInvalid:
        return t('exceptions:Schedule.RecipientInvalid');
      case ControllerExceptionCodes.Code_Schedule_DeliveryGrantRequired:
        return t('exceptions:Schedule.DeliveryGrantRequired');
      case ControllerExceptionCodes.Code_Schedule_RecurrenceUnusable:
        return t('exceptions:Schedule.RecurrenceUnusable');
      case ControllerExceptionCodes.Code_Schedule_RunFailed:
        return t('exceptions:Schedule.RunFailed');
      case ControllerExceptionCodes.Code_Schedule_DeliveryFailed:
        return t('exceptions:Schedule.DeliveryFailed');
      case ControllerExceptionCodes.Code_Schedule_QueueFailed:
        return t('exceptions:Schedule.QueueFailed');
      case ControllerExceptionCodes.Code_Proposal_RequestInvalid:
        return t('exceptions:Proposal.RequestInvalid');
      case ControllerExceptionCodes.Code_Proposal_NotAllowed:
        return t('exceptions:Proposal.NotAllowed');
      case ControllerExceptionCodes.Code_Proposal_ExecutionNotProposable:
        return t('exceptions:Proposal.ExecutionNotProposable');
      case ControllerExceptionCodes.Code_Proposal_AlreadyDecided:
        return t('exceptions:Proposal.AlreadyDecided');
      case ControllerExceptionCodes.Code_Proposal_ExecutionNotFixable:
        return t('exceptions:Proposal.ExecutionNotFixable');
      case ControllerExceptionCodes.Code_Proposal_RequestOpen:
        return t('exceptions:Proposal.RequestOpen');
      case ControllerExceptionCodes.Code_Pack_NotAPublisher:
        return t('exceptions:Pack.NotAPublisher');
      case ControllerExceptionCodes.Code_Pack_RequestInvalid:
        return t('exceptions:Pack.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Pack_KeyExists:
        return t('exceptions:Pack.KeyExists');
      case ControllerExceptionCodes.Code_Pack_ContentNotPublishable:
        return t('exceptions:Pack.ContentNotPublishable', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Pack_VersionNotInState:
        return t('exceptions:Pack.VersionNotInState', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Suggestion_RequestInvalid:
        return t('exceptions:Suggestion.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Suggestion_NotInState:
        return t('exceptions:Suggestion.NotInState');
      case ControllerExceptionCodes.Code_Suggestion_CatalogEntryInvalid:
        return t('exceptions:Suggestion.CatalogEntryInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Suggestion_CatalogKeyExists:
        return t('exceptions:Suggestion.CatalogKeyExists');
      case ControllerExceptionCodes.Code_Suggestion_DraftFailed:
        return t('exceptions:Suggestion.DraftFailed', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Data_SpecInvalid:
        return t('exceptions:Data.SpecInvalid');
      case ControllerExceptionCodes.Code_Data_OutputNotFound:
        return t('exceptions:Data.OutputNotFound');
      case ControllerExceptionCodes.Code_Data_OutputUnavailableAfterPause:
        return t('exceptions:Data.OutputUnavailableAfterPause');
      case ControllerExceptionCodes.Code_Data_InputTooLarge:
        return t('exceptions:Data.InputTooLarge');
      case ControllerExceptionCodes.Code_Data_ResultTooLarge:
        return t('exceptions:Data.ResultTooLarge');
      case ControllerExceptionCodes.Code_Data_CallLimitReached:
        return t('exceptions:Data.CallLimitReached');
      case ControllerExceptionCodes.Code_Data_TypeMismatch:
        return t('exceptions:Data.TypeMismatch');
      case ControllerExceptionCodes.Code_Data_NotJson:
        return t('exceptions:Data.NotJson');
      case ControllerExceptionCodes.Code_Data_DivisionByZero:
        return t('exceptions:Data.DivisionByZero');
      case ControllerExceptionCodes.Code_Data_ChartInvalid:
        return t('exceptions:Data.ChartInvalid');
      case ControllerExceptionCodes.Code_Data_ChartTooLarge:
        return t('exceptions:Data.ChartTooLarge');
      case ControllerExceptionCodes.Code_Credit_NoCreditsAvailable:
        return t('exceptions:Credit.NoCreditsAvailable');
      case ControllerExceptionCodes.Code_Credit_MailNotSetUp:
        return t('exceptions:Credit.MailNotSetUp');
      case ControllerExceptionCodes.Code_Credit_PackageNotAvailable:
        return t('exceptions:Credit.PackageNotAvailable', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Credit_PackageRequestInvalid:
        return t('exceptions:Credit.PackageRequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Billing_PlanNotAvailable:
        return t('exceptions:Billing.PlanNotAvailable', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Billing_PlanRequestInvalid:
        return t('exceptions:Billing.PlanRequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Billing_NoPlan:
        return t('exceptions:Billing.NoPlan', { detail: message ?? '' });
      //the API's sentence names the document; a caller without one (a schedule's warning line) shows the text alone
      case ControllerExceptionCodes.Code_Library_NotInLibrary:
        return [t('exceptions:Library.NotInLibrary'), message].filter(Boolean).join(' ');
      case ControllerExceptionCodes.Code_Library_NoReadGrant:
        return [t('exceptions:Library.NoReadGrant'), message].filter(Boolean).join(' ');
      case ControllerExceptionCodes.Code_Library_NotFound:
        return [t('exceptions:Library.NotFound'), message].filter(Boolean).join(' ');
      case ControllerExceptionCodes.Code_Library_LimitReached:
        return t('exceptions:Library.LimitReached');
      case ControllerExceptionCodes.Code_Library_ExtractionFailed:
        return t('exceptions:Library.ExtractionFailed');
      case ControllerExceptionCodes.Code_Library_TypeNotReadable:
        return t('exceptions:Library.TypeNotReadable');
      case ControllerExceptionCodes.Code_Library_NameTaken:
        return t('exceptions:Library.NameTaken');
      case ControllerExceptionCodes.Code_Library_RequestInvalid:
        return t('exceptions:Library.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_Library_ConnectionNotSupported:
        return t('exceptions:Library.ConnectionNotSupported');
      case ControllerExceptionCodes.Code_App_NotConfigured:
        return t('exceptions:App.NotConfigured');
      case ControllerExceptionCodes.Code_App_RequestInvalid:
        return t('exceptions:App.RequestInvalid', { detail: message ?? '' });
      case ControllerExceptionCodes.Code_App_InstallationNotFound:
        return t('exceptions:App.InstallationNotFound');
      case ControllerExceptionCodes.Code_App_InstallationClaimed:
        return t('exceptions:App.InstallationClaimed');
      case ControllerExceptionCodes.Code_App_InstallationUnavailable:
        return t('exceptions:App.InstallationUnavailable');
      case ControllerExceptionCodes.Code_App_ProviderUnavailable:
        return t('exceptions:App.ProviderUnavailable');
      default:
        return t('exceptions:General.UnknownError');
    }
  }

  // A text the API writes as "CODE: reason" or as the code alone (a schedule's lastError): the code's text in the
  // reader's language, with the API's reason as the detail when that text does not already contain it. A text without
  // a leading code, or with a code this client does not know, comes back as written.
  static getCodedText(text: string, t: TFunction<string[]>): { message: string; detail?: string } {
    const match = /^([A-Z]+[0-9]+)(?::\s*([\s\S]*))?$/.exec(text.trim());
    const code = match?.[1] as ControllerExceptionCodes | undefined;
    if (!code || !Object.values(ControllerExceptionCodes).includes(code)) return { message: text };

    const reason = match?.[2]?.trim() || undefined;
    const message = ControllerException.getMessage(code, t, reason);
    if (message === t('exceptions:General.UnknownError')) return { message: text };

    return { message, detail: reason && !message.includes(reason) ? reason : undefined };
  }
}
